import type {
  CurrentReleasePointer,
  LoadedPublicIrSite,
  PublishedIrSite,
} from './types'

const COMPANY_SLUG_PATTERN = /^[a-z0-9]+(?:[-_][a-z0-9]+)*$/

export function getCompanySlug(pathname: string): string | null {
  const pathParts = pathname.split('/').filter(Boolean)
  const candidate =
    pathParts[0] === 'investor-relations' ? pathParts[1] : pathParts[0]
  return candidate && COMPANY_SLUG_PATTERN.test(candidate) ? candidate : null
}

export function getDocumentDeepLink(
  pathname: string
): { section: string; documentId: string } | null {
  const pathParts = pathname.split('/').filter(Boolean)
  const offset = pathParts[0] === 'investor-relations' ? 2 : 1
  const section = pathParts[offset]
  const documentId = pathParts[offset + 1]
  return section && documentId
    ? { section, documentId: decodeURIComponent(documentId) }
    : null
}

function isSafeSitePath(sitePath: string, releaseId: string): boolean {
  return (
    sitePath === `releases/${releaseId}/site.json` &&
    !sitePath.startsWith('/') &&
    !sitePath.split('/').includes('..')
  )
}

function assertPointer(value: unknown): CurrentReleasePointer {
  if (!value || typeof value !== 'object') {
    throw new Error('The release pointer is invalid.')
  }
  const pointer = value as Partial<CurrentReleasePointer>
  if (
    pointer.schemaVersion !== 1 ||
    typeof pointer.releaseId !== 'string' ||
    typeof pointer.sitePath !== 'string' ||
    !isSafeSitePath(pointer.sitePath, pointer.releaseId)
  ) {
    throw new Error('The release pointer is invalid.')
  }
  return pointer as CurrentReleasePointer
}

function assertSite(value: unknown, companySlug: string): PublishedIrSite {
  if (!value || typeof value !== 'object') {
    throw new Error('The published IR website is invalid.')
  }
  const site = value as Partial<PublishedIrSite>
  if (
    site.schemaVersion !== 1 ||
    site.companySlug !== companySlug ||
    typeof site.releaseId !== 'string' ||
    !site.company ||
    !site.theme ||
    !site.documents
  ) {
    throw new Error('The published IR website is invalid.')
  }
  return site as PublishedIrSite
}

async function getJson(url: string, cache: RequestCache): Promise<unknown> {
  const response = await fetch(url, { cache })
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('This investor relations website has not been published.')
    }
    throw new Error('The investor relations website could not be loaded.')
  }
  return response.json()
}

export async function loadPublicIrSite(
  companySlug: string
): Promise<LoadedPublicIrSite> {
  if (!COMPANY_SLUG_PATTERN.test(companySlug)) {
    throw new Error('The company address is invalid.')
  }

  const pointerUrl = `/sites/${companySlug}/current.json`
  const pointer = assertPointer(await getJson(pointerUrl, 'no-store'))
  const siteDocumentUrl = new URL(
    pointer.sitePath,
    window.location.origin + pointerUrl
  )
  if (siteDocumentUrl.origin !== window.location.origin) {
    throw new Error('The release location is invalid.')
  }
  const site = assertSite(
    await getJson(siteDocumentUrl.pathname, 'force-cache'),
    companySlug
  )
  if (site.releaseId !== pointer.releaseId) {
    throw new Error('The release pointer does not match the published website.')
  }
  return { site, siteDocumentUrl: siteDocumentUrl.toString() }
}

export function resolveReleaseUrl(
  siteDocumentUrl: string,
  relativePath?: string | null
): string | null {
  if (!relativePath) return null
  const resolved = new URL(relativePath, siteDocumentUrl)
  return resolved.origin === window.location.origin ? resolved.toString() : null
}
